import React, { useState, useEffect } from 'react';
import { getContacts, getTrainings, getApplications } from '../services/api';
import toast from 'react-hot-toast';

const AdminInquiries = () => {
  const [activeTab, setActiveTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'contacts') {
        const data = await getContacts();
        setContacts(data);
      } else if (activeTab === 'trainings') {
        const data = await getTrainings();
        setTrainings(data);
      } else if (activeTab === 'careers') {
        const data = await getApplications();
        setApplications(data);
      }
    } catch (error) {
      toast.error('Error fetching data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'contacts', label: 'Contact Messages', icon: 'fas fa-envelope', count: contacts.length },
    { id: 'trainings', label: 'Training Enrollments', icon: 'fas fa-graduation-cap', count: trainings.length },
    { id: 'careers', label: 'Job Applications', icon: 'fas fa-briefcase', count: applications.length }
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending: { background: '#FEF3C7', color: '#D97706' },
      replied: { background: '#D1FAE5', color: '#059669' },
      confirmed: { background: '#D1FAE5', color: '#059669' },
      reviewed: { background: '#DBEAFE', color: '#2563EB' },
      shortlisted: { background: '#D1FAE5', color: '#059669' },
      rejected: { background: '#FEE2E2', color: '#DC2626' },
      cancelled: { background: '#FEE2E2', color: '#DC2626' },
      archived: { background: '#F3F4F6', color: '#374151' }
    };
    const style = styles[status] || { background: '#F3F4F6', color: '#374151' };
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '20px',
        fontSize: '0.75rem',
        background: style.background,
        color: style.color
      }}>
        {status}
      </span>
    );
  };

  const filteredData = () => {
    const data = activeTab === 'contacts' ? contacts : activeTab === 'trainings' ? trainings : applications;
    return data.filter(item => {
      const searchString = `${item.name || item.fullName || ''} ${item.email || ''} ${item.subject || item.courseName || item.position || ''}`.toLowerCase();
      return searchString.includes(searchTerm.toLowerCase());
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="spinner" style={{
          width: '40px',
          height: '40px',
          border: '4px solid #E2E8F0',
          borderTopColor: '#2C7A7B',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem'
        }}></div>
        <p>Loading...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#0A192F', marginBottom: '0.5rem' }}>Inquiries Management</h2>
        <p style={{ color: '#5A6E7F' }}>Manage all contact messages, training enrollments, and job applications</p>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        borderBottom: '2px solid #E2E8F0',
        marginBottom: '2rem',
        flexWrap: 'wrap'
      }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === tab.id ? '#2C7A7B' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#5A6E7F',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <i className={tab.icon}></i>
            {tab.label}
            <span style={{
              background: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : '#E2E8F0',
              padding: '0.2rem 0.5rem',
              borderRadius: '20px',
              fontSize: '0.75rem'
            }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ position: 'relative', maxWidth: '300px' }}>
          <i className="fas fa-search" style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#94A3B8'
          }}></i>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.5rem 0.5rem 2rem',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              fontSize: '0.875rem'
            }}
          />
        </div>
      </div>

      {/* Contacts Table */}
      {activeTab === 'contacts' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Phone</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Subject</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData().map((contact) => (
                <tr key={contact._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '1rem', fontSize: '0.8rem' }}>{new Date(contact.createdAt).toLocaleDateString()}</td>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>{contact.name}</td>
                  <td style={{ padding: '1rem' }}>{contact.email}</td>
                  <td style={{ padding: '1rem' }}>{contact.phone}</td>
                  <td style={{ padding: '1rem' }}>{contact.subject}</td>
                  <td style={{ padding: '1rem' }}>{getStatusBadge(contact.status)}</td>
                  <td style={{ padding: '1rem' }}>
                    <button
                      onClick={() => setSelectedItem(contact)}
                      style={{ padding: '0.25rem 0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredData().length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', background: 'white' }}>
              <p>No contacts found</p>
            </div>
          )}
        </div>
      )}

      {/* Trainings Table */}
      {activeTab === 'trainings' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Phone</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Course</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData().map((training) => (
                <tr key={training._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '1rem', fontSize: '0.8rem' }}>{new Date(training.enrolledAt).toLocaleDateString()}</td>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>{training.fullName}</td>
                  <td style={{ padding: '1rem' }}>{training.email}</td>
                  <td style={{ padding: '1rem' }}>{training.phone}</td>
                  <td style={{ padding: '1rem' }}>{training.courseName}</td>
                  <td style={{ padding: '1rem' }}>{getStatusBadge(training.status)}</td>
                  <td style={{ padding: '1rem' }}>
                    <button
                      onClick={() => setSelectedItem(training)}
                      style={{ padding: '0.25rem 0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Careers Table */}
      {activeTab === 'careers' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <thead style={{ background: '#F7F9FC' }}>
              <tr>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Phone</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Position</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData().map((application) => (
                <tr key={application._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '1rem', fontSize: '0.8rem' }}>{new Date(application.appliedAt).toLocaleDateString()}</td>
                  <td style={{ padding: '1rem', fontWeight: 500 }}>{application.fullName}</td>
                  <td style={{ padding: '1rem' }}>{application.email}</td>
                  <td style={{ padding: '1rem' }}>{application.phone}</td>
                  <td style={{ padding: '1rem' }}>{application.position}</td>
                  <td style={{ padding: '1rem' }}>{getStatusBadge(application.status)}</td>
                  <td style={{ padding: '1rem' }}>
                    <button
                      onClick={() => setSelectedItem(application)}
                      style={{ padding: '0.25rem 0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View Modal */}
      {selectedItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setSelectedItem(null)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: '1rem' }}>Details</h3>
            <pre style={{ background: '#F7F9FC', padding: '1rem', borderRadius: '8px', overflow: 'auto' }}>
              {JSON.stringify(selectedItem, null, 2)}
            </pre>
            <button
              onClick={() => setSelectedItem(null)}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                background: '#2C7A7B',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInquiries;